import { api } from "@/api/utils";
import { PageInset } from "@/components/PageInset";
import { TokenAmount } from "@/components/TokenAmount";
import { TopNavigation, TopNavigationBackButton } from "@/components/ui/TopNavigation";
import { createElement, init } from "@airwallex/components-sdk";
import { config } from "@common/utils/config";
import { formatCents } from "@common/utils/i18n";
import { Box, Table, Tbody, Td, Tr } from "@givenwell/components";
import { QuoteAmount } from "@givenwell/marketplace-api";
import { colors } from "@givenwell/theme";
import {
  createFileRoute,
  getRouteApi,
  notFound,
  useCanGoBack,
  useLinkProps,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, useId } from "react";

export const Route = createFileRoute("/_auth/_app/top-up/checkout/$tokenAmount")({
  loader: async ({ params }) => {
    const tokenAmount = Number(params.tokenAmount);
    if (!isFinite(tokenAmount)) {
      throw notFound();
    }
    const integerTokenAmount = Math.floor(tokenAmount);
    if (tokenAmount !== integerTokenAmount) {
      throw notFound();
    }
    if (integerTokenAmount < 1) {
      throw notFound();
    }
    const quoteResponse = await api.topup.quoteTokenCosts({
      tokenAmount: [integerTokenAmount],
    });
    const quote = quoteResponse.quotes[0];
    if (!quote) {
      throw notFound();
    }
    const topupPurchase = await api.topup.startTopupPurchase({
      requestBody: { token_amount: integerTokenAmount },
    });

    return { quote, topupPurchase };
  },
  // Do not cache this route's data after it's unloaded
  gcTime: 0,
  // Only reload the route when the user navigates to it or when deps change
  shouldReload: false,
  component: CheckoutPage,
});

type TopupAmounts = QuoteAmount;

const route = getRouteApi("/_auth/_app/top-up/checkout/$tokenAmount");

function CheckoutPage() {
  const { topupPurchase, quote } = route.useLoaderData();
  const navigate = useNavigate();

  const { "return-to": returnUrlFromSearch } = route.useSearch();

  const defaultReturnHref = useLinkProps({
    to: "/wallet",
  }).href;

  const returnHref = returnUrlFromSearch || defaultReturnHref || "/";

  return (
    <Box css={{ display: "flex", flexDirection: "column", flex: "1 0 0px", pt: "var(--safe-area-inset-top)" }}>
      <Box css={{ width: "100%", maxW: 600 + 32, mx: "auto" }}>
        <Header />
      </Box>
      <Box css={{ flex: "1 0 0px", overflowY: "auto" }}>
        <PageInset>
          <Box css={{ width: "100%", maxW: 600, mx: "auto" }}>
            <Box css={{ mt: 16, minHeight: 580 }}>
              <AirwallexSplitCard
                clientSecret={topupPurchase.client_secret}
                currency={topupPurchase.currency}
                onPaymentSuccess={() => {
                  navigate({
                    to: "/top-up/complete",
                    search: {
                      "return-to": returnHref,
                    },
                    replace: true,
                  });
                }}
                paymentIntentId={topupPurchase.intent_id}
              />
              <Box css={{ h: 40 }} />

              <PaymentSummary amounts={quote} />
              <Box css={{ h: "max(var(--safe-area-inset-bottom), var(--layout-margin))" }} />
            </Box>
          </Box>
        </PageInset>
        {/* <PersistentBottomSheet estimatedHeight={100}>
          <Button
            type="submit"
            variant="filled"
            color="primary"
            size="lg"
            form={formId}
            css={{ width: "100%" }}
            loading={isProcessing}
            loadingText="Please wait..."
          >
            Pay Now
          </Button>
        </PersistentBottomSheet> */}
      </Box>
    </Box>
  );
}

function Header() {
  const canGoBack = useCanGoBack();
  const navigate = useNavigate();
  const router = useRouter();
  return (
    <TopNavigation
      title="Top Up Your Wallet"
      left={
        <TopNavigationBackButton
          onClick={e => {
            if (canGoBack) {
              e.preventDefault();
              router.history.back();
            } else {
              navigate({ to: "/top-up", replace: true });
            }
          }}
        />
      }
    />
  );
}

type PaymentSummaryProps = {
  amounts: TopupAmounts | undefined;
};
function PaymentSummary({ amounts }: PaymentSummaryProps) {
  if (!amounts) {
    return (
      <Table>
        <Tbody>
          <Tr>
            <Td css={{ width: "100%" }}>Top up amount</Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Subtotal</Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Card fee</Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Includes GST of</Td>
          </Tr>
          <Tr css={{ fontWeight: "bold" }}>
            <Td css={{ width: "100%" }}>Total</Td>
          </Tr>
        </Tbody>
      </Table>
    );
  }

  return (
    <Box css={{ backgroundColor: "#CCE8E7", p: 16, borderRadius: 12 }}>
      <Table>
        <Tbody>
          <Tr>
            <Td
              colSpan={2}
              css={{
                width: "100%",
                fontScale: "lg",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              <TokenAmount amount={amounts.token_amount} withText />
            </Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Subtotal</Td>
            <Td css={{ align: "right" }}>
              {formatCents(amounts.purchase_price - amounts.payment_fee_amount, amounts.currency, true)}
            </Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Card fee</Td>
            <Td css={{ align: "right" }}>{formatCents(amounts.payment_fee_amount, amounts.currency, true)}</Td>
          </Tr>
          <Tr>
            <Td css={{ width: "100%" }}>Includes GST of</Td>
            <Td css={{ align: "right" }}>{formatCents(amounts.tax_amount, amounts.currency, true)}</Td>
          </Tr>
          <Tr css={{ fontWeight: "bold" }}>
            <Td css={{ width: "100%" }}>Total</Td>
            <Td css={{ align: "right", whiteSpace: "nowrap" }}>
              {amounts.currency.toUpperCase()} {formatCents(amounts.purchase_price, amounts.currency, false)}
            </Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  );
}

type AirwallexSplitCardProps = {
  paymentIntentId: string;
  clientSecret: string;
  currency: string;
  onPaymentSuccess: () => void;
};
function AirwallexSplitCard({ paymentIntentId, clientSecret, currency, onPaymentSuccess }: AirwallexSplitCardProps) {
  const id = useId();

  useEffect(() => {
    init({
      env: config.environmentName === "prod" ? "prod" : "demo",
      enabledElements: ["payments"],
      fonts: [
        {
          src: "https://fonts.gstatic.com/s/dmsans/v15/rP2tp2ywxg089UriI5-g4vlH9VoD8CmcqZG40F9JadbnoEwAopxRSW32RmYJp8I5.woff2",
          family: "DM Sans",
          weight: "400",
        },
      ],
    })
      .then(() => {
        return createElement("dropIn", {
          intent_id: paymentIntentId,
          client_secret: clientSecret,
          currency: currency.toUpperCase(),
          methods: ["card"],
          style: {
            base: {
              fontFamily: "'DM Sans', sans-serif",
              background: colors.cream,
            },
            input: {
              background: "#FFFFFF",
            },
          },
          theme: {
            palette: {
              primary: colors.navy,
            },
          },
          requiredBillingContactFields: ["address"],
        });
      })
      .then(element => {
        element.mount(id);

        element.on("success", onPaymentSuccess);
        element.on("error", e => {
          console.error("Airwallex payment error", e);
        });
      });
  }, [clientSecret, currency, id, onPaymentSuccess, paymentIntentId]);

  return (
    <div>
      <div id={id} />
    </div>
  );
}
